import React from 'react';
import { Dropdown } from 'antd';
import { ColumnsType } from 'antd/es/table';
import { Link } from 'react-router-dom';

import { getTableSortOrder } from '../../common/getTableSortOrder';
import { InitialsBadgeTableCell } from '../../components/InitialsBadgeTableCell';
import { LinkTableCell } from '../../components/LinkTableCell';
import Icon from '../../icons/Icon';
import { pathToUrl } from '../../utils/router';
import { PageRoutesEnum } from '../../utils/routes';

import { RecruitmentsDataType } from './useRecruitmentsTableDataSource';

const getMenuItems = (record: RecruitmentsDataType) => [
  {
    key: record.candidateId,
    label: <Link to={pathToUrl(PageRoutesEnum.candidateEdit, { id: record.candidateId })}>Go to Candidate</Link>,
  },
  {
    key: record.clientId,
    label: <Link to={pathToUrl(PageRoutesEnum.clientEdit, { id: record.clientId })}>Go to Client</Link>,
  },
  {
    key: record.projectId,
    label: <Link to={pathToUrl(PageRoutesEnum.projectsEdit, { id: record.projectId })}>Go to Project</Link>,
  },
];

const renderMenuCell = (text: string, record: RecruitmentsDataType) => {
  const items = getMenuItems(record);
  return (
    <Dropdown menu={{ items }} trigger={['click']}>
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
        <a onClick={(e) => e.preventDefault()}>
          <Icon name="Dots" />
        </a>
      </div>
    </Dropdown>
  );
};

const renderLinkCell = (text: string, record: RecruitmentsDataType) => (
  <LinkTableCell text={text} record={record} pageRoute={PageRoutesEnum.recruitmentEdit} />
);

const renderResponsibleLinkCell = (text: string, record: RecruitmentsDataType) => {
  return (
    <Link to={pathToUrl(PageRoutesEnum.recruitmentEdit, { id: record.key })} style={{ color: 'black' }}>
      <InitialsBadgeTableCell userName={record.responsible} />
    </Link>
  );
};

const renderPerfectMatchLinkCell = (text: string, record: RecruitmentsDataType) => {
  return (
    <Link to={pathToUrl(PageRoutesEnum.recruitmentEdit, { id: record.key })} style={{ color: 'black' }}>
      <div style={{ width: '100%', padding: '16px' }}>{record.perfectMatch ? 'Yes' : ''}</div>
    </Link>
  );
};

const candidateColumns = (sortedInfo: { field: string; order: string }[]) => [
  {
    title: 'Candidate',
    dataIndex: 'candidate',
    render: renderLinkCell,
    width: 180,
  },
  {
    title: 'Recommended by',
    dataIndex: 'candidate.recommendedBy',
    sorter: {
      compare: () => 0,
      multiple: 1,
    },
    render: renderLinkCell,
    sortOrder: getTableSortOrder(sortedInfo, 'candidate.recommendedBy'),
    width: 160,
  },
];

const salaryDescriptionColumn = {
  title: 'Salary description',
  dataIndex: 'salaryDescription',
  render: renderLinkCell,
  width: 180,
};

const perfectMatchColumn = (sortedInfo: { field: string; order: string }[]) => ({
  title: 'Perfect match',
  dataIndex: 'perfectMatch',
  render: renderPerfectMatchLinkCell,
  width: 95,
  sorter: {
    compare: () => 0,
    multiple: 1,
  },
  sortOrder: getTableSortOrder(sortedInfo, 'perfectMatch'),
});

const projectColumns = (sortedInfo: { field: string; order: string }[]) => [
  {
    title: 'Client',
    dataIndex: 'project.client.name',
    sorter: {
      compare: () => 0,
      multiple: 1,
    },
    render: renderLinkCell,
    sortOrder: getTableSortOrder(sortedInfo, 'project.client.name'),
    width: 170,
  },
  {
    title: 'Project name',
    dataIndex: 'project.name',
    sorter: {
      compare: () => 0,
      multiple: 1,
    },
    render: renderLinkCell,
    sortOrder: getTableSortOrder(sortedInfo, 'project.name'),
    width: 170,
  },
  {
    title: 'Priority',
    dataIndex: 'project.priority',
    sorter: {
      compare: () => 0,
      multiple: 1,
    },
    render: renderLinkCell,
    width: 120,
    sortOrder: getTableSortOrder(sortedInfo, 'project.priority'),
  },
];

const basicColumns = ({
  sortedInfo,
}: {
  sortedInfo: { field: string; order: string }[];
  withLocalSorting?: boolean;
}) => {
  return [
    {
      title: '',
      dataIndex: '',
      width: 20,
      render: renderMenuCell,
    },
    {
      title: 'Status',
      dataIndex: 'status.name',
      sorter: {
        compare: () => 0,
        multiple: 1,
      },
      render: renderLinkCell,
      sortOrder: getTableSortOrder(sortedInfo, 'status.name'),
      width: 120,
    },
    {
      title: 'Responsible',
      dataIndex: 'responsible',
      render: renderResponsibleLinkCell,
      width: 110,
    },
    {
      title: 'Created',
      dataIndex: 'createdDate',
      sorter: {
        compare: () => 0,
        multiple: 1,
      },
      render: renderLinkCell,
      sortOrder: getTableSortOrder(sortedInfo, 'createdDate'),
      width: 140,
    },
    {
      title: 'Last modified',
      dataIndex: 'lastModifiedDate',
      sorter: {
        compare: () => 0,
        multiple: 1,
      },
      render: renderLinkCell,
      sortOrder: getTableSortOrder(sortedInfo, 'lastModifiedDate'),
      width: 140,
    },
  ];
};

export const useRecruitmentsTableColumnDefinitions = ({
  sortedInfo,
}: {
  sortedInfo: { field: string; order: string }[];
  withLocalSorting?: boolean;
}): ColumnsType<RecruitmentsDataType> => {
  return [
    ...candidateColumns(sortedInfo),
    ...projectColumns(sortedInfo),
    ...basicColumns({ sortedInfo }),
    salaryDescriptionColumn,
    perfectMatchColumn(sortedInfo),
  ];
};

export const useCandidateRecruitmentsTableColumnDefinitions = ({
  sortedInfo,
}: {
  sortedInfo: { field: string; order: string }[];
  withLocalSorting?: boolean;
}): ColumnsType<RecruitmentsDataType> => {
  return [...projectColumns(sortedInfo), ...basicColumns({ sortedInfo })];
};

export const useProjectRecruitmentsTableColumnDefinitions = ({
  sortedInfo,
}: {
  sortedInfo: { field: string; order: string }[];
  withLocalSorting?: boolean;
}): ColumnsType<RecruitmentsDataType> => {
  return [...candidateColumns(sortedInfo), ...basicColumns({ sortedInfo }), salaryDescriptionColumn];
};
